import { DBTicket, NearByTicket } from '@parkingbase/ecommerce-api-test';

import type { RawAxiosRequestConfig } from 'axios';
import { create } from 'zustand';
import { TicketService } from '@/services/ticket.service';
import { logger } from '@/lib/logger';

const ticketService = new TicketService();

interface TicketState {
  nearbyTickets: NearByTicket[];
  searchedTicket: DBTicket | null;
  isSearching: boolean;
  error: string | null;
  searchByPlate: (lat: number, lng: number, plate: string, options?: RawAxiosRequestConfig) => Promise<void>;
  searchTicket: (ticketNumber?: string, licensePlateNumber?: string, options?: RawAxiosRequestConfig) => Promise<void>;
  clearResults: () => void;
  reset: () => void;
}

export const useTicketStore = create<TicketState>((set) => ({
  nearbyTickets: [],
  searchedTicket: null,
  isSearching: false,
  error: null,

  searchByPlate: async (lat: number, lng: number, plate: string, options?: RawAxiosRequestConfig) => {
    try {
      set({ isSearching: true, error: null, nearbyTickets: [] });
      const data = await ticketService.getNearbyTransientTickets(lat, lng, plate, options);
      const normalizedPlate = plate.toLowerCase().replace(/\s/g, '');
      const filtered = (data || []).filter(
        (ticket) => ticket.plate?.toLowerCase().replace(/\s/g, '') === normalizedPlate
      );
      set({ nearbyTickets: filtered });
    } catch (error) {
      set({ error: 'failedToSearchTickets' });
      logger.error('Failed to search nearby tickets', error, {
        feature: 'ticket-store',
        route: 'searchByPlate',
      });
    } finally {
      set({ isSearching: false });
    }
  },

  searchTicket: async (ticketNumber?: string, licensePlateNumber?: string, options?: RawAxiosRequestConfig) => {
    try {
      set({ isSearching: true, error: null, searchedTicket: null });
      const data = await ticketService.searchTicket(ticketNumber, undefined, licensePlateNumber, options);
      set({ searchedTicket: data || null });
    } catch (error) {
      set({ error: 'failedToSearchTickets' });
      logger.error('Failed to search ticket', error, {
        feature: 'ticket-store',
        route: 'searchTicket',
      });
    } finally {
      set({ isSearching: false });
    }
  },

  reset: () => {
    set({ nearbyTickets: [], searchedTicket: null, isSearching: false, error: null });
  },

  clearResults: () => {
    set({ nearbyTickets: [], searchedTicket: null, isSearching: false, error: null });
  },
}));
